<template>
  <div class="right-sidebar-wrapper" :class="{ 'sidebar-open': isOpen }">
    <div class="toggle-btn" @click="toggleSidebar">
      <v-icon :icon="isOpen ? 'mdi-chevron-right' : 'mdi-chevron-left'"></v-icon>
    </div>

    <div class="sidebar-content">
      <v-card class="sidebar-card mb-4">
        <v-card-title class="sidebar-card-title" @click="toggleKurztexteCollapsed">
          <v-icon color="success" class="mr-2">mdi-text-box-outline</v-icon>
          <span>Kurztexte</span>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            density="compact"
            icon="mdi-chevron-down"
            size="small"
            color="default"
            :style="{ transform: !sidebarSettings.kurztexteCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }"
            @click.stop="toggleKurztexteCollapsed"
          ></v-btn>
        </v-card-title>

        <template v-if="!sidebarSettings.kurztexteCollapsed">
          <!-- Suchfeld für Kurztexte -->
          <div class="search-container">
            <v-text-field
              v-model="kurztextSearchQuery"
              label="Suche nach Kurztext..."
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="search-field"
              color="success"
              bg-color="rgba(30, 30, 40, 0.5)"
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
          </div>

          <KurztexteWidget
            :filter-query="kurztextSearchQuery"
            @item-hover="showKurztextTooltip"
            @item-leave="hideTooltip"
            @item-dblclick="showKurztextDetails"
          />
        </template>
      </v-card>

      <v-card class="sidebar-card">
        <v-card-title class="sidebar-card-title" @click="toggleDurchwahlenCollapsed">
          <v-icon color="success" class="mr-2">mdi-phone</v-icon>
          <span>Durchwahlen</span>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            density="compact"
            icon="mdi-chevron-down"
            size="small"
            color="default"
            :style="{ transform: !sidebarSettings.durchwahlenCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }"
            @click.stop="toggleDurchwahlenCollapsed"
          ></v-btn>
        </v-card-title>

        <template v-if="!sidebarSettings.durchwahlenCollapsed">
          <!-- Suchfeld für Durchwahlen -->
          <div class="search-container">
            <v-text-field
              v-model="durchwahlSearchQuery"
              label="Suche nach Durchwahl..."
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="search-field"
              color="success"
              bg-color="rgba(30, 30, 40, 0.5)"
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
          </div>

          <DurchwahlWidget
            :filter-query="durchwahlSearchQuery"
            @item-hover="showDurchwahlTooltip"
            @item-leave="hideTooltip"
            @item-dblclick="showDurchwahlDetails"
          />
        </template>
      </v-card>

      <v-card class="sidebar-card mt-4">
        <v-card-title class="sidebar-card-title" @click="toggleModelleCollapsed">
          <v-icon color="success" class="mr-2">mdi-coffee</v-icon>
          <span>Modelle</span>
          <v-spacer></v-spacer>
          <v-btn
            variant="text"
            density="compact"
            icon="mdi-chevron-down"
            size="small"
            color="default"
            :style="{ transform: !sidebarSettings.modelleCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }"
            @click.stop="toggleModelleCollapsed"
          ></v-btn>
        </v-card-title>

        <template v-if="!sidebarSettings.modelleCollapsed">
          <!-- Suchfeld für Modelle -->
          <div class="search-container">
            <v-text-field
              v-model="modellSearchQuery"
              label="Suche nach Modell..."
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="search-field"
              color="success"
              bg-color="rgba(30, 30, 40, 0.5)"
              prepend-inner-icon="mdi-magnify"
            ></v-text-field>
          </div>

          <ModelleWidget
            :filter-query="modellSearchQuery"
            @item-hover="showModellTooltip"
            @item-leave="hideTooltip"
            @item-dblclick="showModellDetails"
          />
        </template>
      </v-card>
    </div>
  </div>

  <!-- Tooltip-System -->
  <div v-if="tooltip.visible" class="custom-tooltip" :style="tooltipStyle">
    <div v-if="tooltip.type === 'modell'" class="tooltip-content">
      <div class="tooltip-header">{{ tooltip.data.modell }}</div>
      <v-divider class="mb-2"></v-divider>

      <div v-if="tooltip.data.ws_menue" class="tooltip-field">
        <div class="tooltip-label">WS-Menü:</div>
        <div class="tooltip-value">{{ tooltip.data.ws_menue }}</div>
      </div>

      <div v-if="tooltip.data.tassenzahl" class="tooltip-field">
        <div class="tooltip-label">Tassenzahl:</div>
        <div class="tooltip-value">{{ tooltip.data.tassenzahl }}</div>
      </div>

      <div v-if="tooltip.data.kalibrieren" class="tooltip-field">
        <div class="tooltip-label">Kalibrieren:</div>
        <div class="tooltip-value">{{ tooltip.data.kalibrieren }}</div>
      </div>

      <div v-if="tooltip.data.zuruecksetzen" class="tooltip-field">
        <div class="tooltip-label">Zurücksetzen:</div>
        <div class="tooltip-value">{{ tooltip.data.zuruecksetzen }}</div>
      </div>

      <div v-if="tooltip.data.sonstiges" class="tooltip-field">
        <div class="tooltip-label">Sonstiges:</div>
        <div class="tooltip-value">{{ tooltip.data.sonstiges }}</div>
      </div>
    </div>

    <div v-else-if="tooltip.type === 'durchwahl'" class="tooltip-content">
      <div class="tooltip-header">{{ tooltip.data.name }} {{ tooltip.data.vorname || '' }}</div>
      <v-divider class="mb-2"></v-divider>

      <div class="tooltip-field">
        <div class="tooltip-label">Durchwahl:</div>
        <div class="tooltip-value d-flex align-center">
          <strong>{{ tooltip.data.durchwahl }}</strong>
          <v-btn
            icon
            size="x-small"
            variant="text"
            color="success"
            class="ml-2"
            @click="copyToClipboard(tooltip.data.durchwahl)"
          >
            <v-icon size="small">mdi-content-copy</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-if="tooltip.data.abteilung" class="tooltip-field">
        <div class="tooltip-label">Abteilung:</div>
        <div class="tooltip-value">{{ tooltip.data.abteilung }}</div>
      </div>
    </div>

    <div v-else-if="tooltip.type === 'kurztext'" class="tooltip-content">
      <div class="d-flex align-center mb-2">
        <div class="tooltip-header">{{ tooltip.data.bezeichnung || "Kurztext" }}</div>
        <v-spacer></v-spacer>
        <v-chip
          size="x-small"
          class="snippet-number mr-2"
          variant="outlined"
          color="success"
        >
          {{ tooltip.data.nummer }}
        </v-chip>
      </div>
      <v-divider class="mb-2"></v-divider>

      <div class="tooltip-field">
        <div class="tooltip-label">Kurztext:</div>
        <div class="tooltip-value">
          {{ tooltip.data.kurztext }}
        </div>
      </div>

      <v-btn
        variant="outlined"
        color="success"
        size="small"
        class="mt-2"
        prepend-icon="mdi-content-copy"
        @click="copyToClipboard(tooltip.data.kurztext)"
      >
        Kurztext kopieren
      </v-btn>
    </div>
  </div>

  <!-- Detail Dialog -->
  <v-dialog v-model="detailDialog.visible" max-width="600">
    <v-card class="detail-dialog">
      <v-card-title class="dialog-title">
        <span v-if="detailDialog.type === 'modell'">
          {{ detailDialog.data.modell }}
        </span>
        <span v-else-if="detailDialog.type === 'durchwahl'">
          {{ detailDialog.data.name }} {{ detailDialog.data.vorname || '' }}
        </span>
        <span v-else-if="detailDialog.type === 'kurztext'">
          {{ detailDialog.data.bezeichnung || 'Kurztext' }} #{{ detailDialog.data.nummer }}
        </span>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" @click="detailDialog.visible = false"></v-btn>
      </v-card-title>

      <v-card-text class="dialog-content">
        <div v-if="detailDialog.type === 'modell'">
          <v-row>
            <v-col cols="6">
              <div class="dialog-field">
                <div class="dialog-label">WS-Menü:</div>
                <div class="dialog-value">{{ detailDialog.data.ws_menue || 'Nicht angegeben' }}</div>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="dialog-field">
                <div class="dialog-label">Tassenzahl:</div>
                <div class="dialog-value">{{ detailDialog.data.tassenzahl || 'Nicht angegeben' }}</div>
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <div class="dialog-field">
                <div class="dialog-label">Kalibrieren:</div>
                <div class="dialog-value">{{ detailDialog.data.kalibrieren || 'Nicht angegeben' }}</div>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="dialog-field">
                <div class="dialog-label">Zurücksetzen:</div>
                <div class="dialog-value">{{ detailDialog.data.zuruecksetzen || 'Nicht angegeben' }}</div>
              </div>
            </v-col>
          </v-row>
          <div class="dialog-field">
            <div class="dialog-label">Sonstiges:</div>
            <div class="dialog-value dialog-multiline">{{ detailDialog.data.sonstiges || 'Keine weiteren Informationen' }}</div>
          </div>
        </div>

        <div v-else-if="detailDialog.type === 'durchwahl'">
          <v-row>
            <v-col cols="6">
              <div class="dialog-field">
                <div class="dialog-label">Name:</div>
                <div class="dialog-value">{{ detailDialog.data.name }} {{ detailDialog.data.vorname || '' }}</div>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="dialog-field">
                <div class="dialog-label">Durchwahl:</div>
                <div class="dialog-value d-flex align-center">
                  {{ detailDialog.data.durchwahl }}
                  <v-btn
                    icon
                    size="small"
                    variant="text"
                    color="success"
                    class="ml-2"
                    @click="copyToClipboard(detailDialog.data.durchwahl)"
                  >
                    <v-icon size="small">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
          <div class="dialog-field">
            <div class="dialog-label">Abteilung:</div>
            <div class="dialog-value">{{ detailDialog.data.abteilung || 'Keine Abteilung angegeben' }}</div>
          </div>
        </div>

        <div v-else-if="detailDialog.type === 'kurztext'">
          <v-row>
            <v-col cols="6">
              <div class="dialog-field">
                <div class="dialog-label">Nummer:</div>
                <div class="dialog-value">{{ detailDialog.data.nummer }}</div>
              </div>
            </v-col>
            <v-col cols="6">
              <div class="dialog-field">
                <div class="dialog-label">Bezeichnung:</div>
                <div class="dialog-value">{{ detailDialog.data.bezeichnung || 'Ohne Bezeichnung' }}</div>
              </div>
            </v-col>
          </v-row>
          <div class="dialog-field">
            <div class="dialog-label">Kurztext:</div>
            <div class="dialog-value dialog-multiline">{{ detailDialog.data.kurztext }}</div>
            <v-btn
              variant="outlined"
              color="success"
              class="mt-3"
              prepend-icon="mdi-content-copy"
              @click="copyToClipboard(detailDialog.data.kurztext)"
            >
              Kurztext kopieren
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, onMounted, computed, watch, provide, nextTick, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';
import DurchwahlWidget from '@/views/widgets/DurchwahlWidget.vue';
import KurztexteWidget from '@/views/widgets/KurztexteWidget.vue';
import ModelleWidget from '@/views/widgets/ModelleWidget.vue';

// Store und Toast einrichten
const authStore = useAuthStore();
const toast = useToast();
const route = useRoute();

// Tooltip-System
const tooltip = ref({
  visible: false,
  type: null, // 'modell', 'durchwahl', 'kurztext'
  data: {},
  position: { x: 0, y: 0 }
});

// Detail-Dialog-System
const detailDialog = ref({
  visible: false,
  type: null,
  data: {}
});

// Tooltip-Position und Style berechnen
const tooltipStyle = computed(() => {
  return {
    top: `${tooltip.value.position.y}px`,
    left: `${tooltip.value.position.x}px`
  };
});

// Hover-Handler für die Widget-Elemente
const showModellTooltip = (event, model) => {
  tooltip.value.type = 'modell';
  tooltip.value.data = model;
  positionTooltip(event);
  tooltip.value.visible = true;
};

const showDurchwahlTooltip = (event, durchwahl) => {
  tooltip.value.type = 'durchwahl';
  tooltip.value.data = durchwahl;
  positionTooltip(event);
  tooltip.value.visible = true;
};

const showKurztextTooltip = (event, kurztext) => {
  tooltip.value.type = 'kurztext';
  tooltip.value.data = kurztext;
  positionTooltip(event);
  tooltip.value.visible = true;
};

const hideTooltip = () => {
  tooltip.value.visible = false;
};

// Tooltip positionieren
const positionTooltip = (event) => {
  const targetRect = event.target.getBoundingClientRect();

  // Rechts vom Element, vertikal zentriert
  tooltip.value.position = {
    x: targetRect.right + 15,
    y: targetRect.top - 5
  };

  // Anpassung bei Bedarf, wenn der Tooltip über den Bildschirmrand hinausragt
  nextTick(() => {
    const tooltipElement = document.querySelector('.custom-tooltip');
    if (tooltipElement) {
      const tooltipRect = tooltipElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (tooltipRect.right > viewportWidth - 20) {
        // Links vom Element positionieren, wenn rechts kein Platz ist
        tooltip.value.position.x = targetRect.left - tooltipRect.width - 15;
      }

      if (tooltipRect.bottom > viewportHeight - 20) {
        // Tooltip nach oben verschieben, wenn er unten abgeschnitten wäre
        tooltip.value.position.y = viewportHeight - tooltipRect.height - 20;
      }
    }
  });
};

// Doppelklick-Handler für die Detailansicht
const showModellDetails = (model) => {
  detailDialog.value.type = 'modell';
  detailDialog.value.data = model;
  detailDialog.value.visible = true;
};

const showDurchwahlDetails = (durchwahl) => {
  detailDialog.value.type = 'durchwahl';
  detailDialog.value.data = durchwahl;
  detailDialog.value.visible = true;
};

const showKurztextDetails = (kurztext) => {
  detailDialog.value.type = 'kurztext';
  detailDialog.value.data = kurztext;
  detailDialog.value.visible = true;
};

// DOM-Event-Listener für Widget-Items (da wir die Widgets selbst nicht ändern können)
const setupWidgetEventListeners = () => {
  nextTick(() => {
    // Modell-Items
    setupListenersForSelector('.model-item', 'modell');

    // Durchwahl-Items
    setupListenersForSelector('.extension-item', 'durchwahl');

    // Kurztext-Items
    setupListenersForSelector('.snippet-item', 'kurztext');
  });
};

// Event-Listener für bestimmte Selektoren einrichten
const setupListenersForSelector = (selector, type) => {
  const items = document.querySelectorAll(selector);
  items.forEach((item, index) => {
    // Daten-Index als Attribut setzen
    item.setAttribute('data-index', index);

    // Event-Listener hinzufügen
    item.addEventListener('mouseenter', (e) => handleItemHover(e, type, index));
    item.addEventListener('mouseleave', hideTooltip);
    item.addEventListener('dblclick', (e) => handleItemDblClick(e, type, index));
  });
};

// Event-Handler für Hover
const handleItemHover = (event, type, index) => {
  let data;

  switch (type) {
    case 'modell':
      data = displayedModelle.value[index];
      if (data) showModellTooltip(event, data);
      break;
    case 'durchwahl':
      data = displayedDurchwahlen.value[index];
      if (data) showDurchwahlTooltip(event, data);
      break;
    case 'kurztext':
      data = displayedKurztexte.value[index];
      if (data) showKurztextTooltip(event, data);
      break;
  }
};

// Event-Handler für Doppelklick
const handleItemDblClick = (event, type, index) => {
  let data;

  switch (type) {
    case 'modell':
      data = displayedModelle.value[index];
      if (data) showModellDetails(data);
      break;
    case 'durchwahl':
      data = displayedDurchwahlen.value[index];
      if (data) showDurchwahlDetails(data);
      break;
    case 'kurztext':
      data = displayedKurztexte.value[index];
      if (data) showKurztextDetails(data);
      break;
  }
};

// Zustandsvariablen
const isOpen = ref(false);
const sidebarSettings = ref({
  isOpen: false,
  kurztexteCollapsed: false,
  durchwahlenCollapsed: false,
  modelleCollapsed: false
});

const toggleModelleCollapsed = () => {
  sidebarSettings.value.modelleCollapsed = !sidebarSettings.value.modelleCollapsed;
  saveSidebarSettings();
  // Event-Listener neu einrichten, wenn Panel geöffnet wird
  if (!sidebarSettings.value.modelleCollapsed) {
    nextTick(() => setupWidgetEventListeners());
  }
};

// Durchwahlen und Kurztexte-Daten
const globalDurchwahlen = ref([]);
const showAllDurchwahlen = ref(false);
const globalKurztexte = ref([]);
const showAllKurztexte = ref(false);

// Suchvariablen
const durchwahlSearchQuery = ref('');
const kurztextSearchQuery = ref('');

// Berechnen der angezeigten Datensätze
const displayedDurchwahlen = computed(() => {
  // Filtern nach Suchbegriff
  let filtered = globalDurchwahlen.value;
  if (durchwahlSearchQuery.value) {
    const query = durchwahlSearchQuery.value.toLowerCase();
    filtered = filtered.filter(
      item => item.name.toLowerCase().includes(query) ||
              item.vorname?.toLowerCase().includes(query) ||
              item.durchwahl.toLowerCase().includes(query) ||
              item.abteilung?.toLowerCase().includes(query)
    );
  }
  // Bei aktivierter "Alle anzeigen" Option oder weniger als 5 Ergebnissen alle anzeigen
  if (showAllDurchwahlen.value || filtered.length <= 4) {
    return filtered;
  }
  // Sonst nur die ersten 4
  return filtered.slice(0, 4);
});

const displayedKurztexte = computed(() => {
  // Filtern nach Suchbegriff
  let filtered = globalKurztexte.value;
  if (kurztextSearchQuery.value) {
    const query = kurztextSearchQuery.value.toLowerCase();
    filtered = filtered.filter(
      item => item.kurztext.toLowerCase().includes(query) ||
              (item.nummer ? String(item.nummer).toLowerCase().includes(query) : false) ||
              (item.bezeichnung && item.bezeichnung.toLowerCase().includes(query))
    );
  }
  // Bei aktivierter "Alle anzeigen" Option oder weniger als 5 Ergebnissen alle anzeigen
  if (showAllKurztexte.value || filtered.length <= 4) {
    return filtered;
  }
  // Sonst nur die ersten 4
  return filtered.slice(0, 4);
});

// Text kürzen für Anzeige
const truncateText = (text, length) => {
  if (!text) return "";
  return text.length > length ? text.substring(0, length) + "..." : text;
};

// Umschalten der Anzeige
const toggleShowAllDurchwahlen = () => {
  showAllDurchwahlen.value = !showAllDurchwahlen.value;
  // Event-Listener nach Änderung der angezeigten Items neu einrichten
  nextTick(() => setupWidgetEventListeners());
};

const toggleShowAllKurztexte = () => {
  showAllKurztexte.value = !showAllKurztexte.value;
  // Event-Listener nach Änderung der angezeigten Items neu einrichten
  nextTick(() => setupWidgetEventListeners());
};

// Text in die Zwischenablage kopieren
const copyToClipboard = (text) => {
  try {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`Text in die Zwischenablage kopiert!`);
      })
      .catch((err) => {
        console.error("Fehler beim Kopieren in die Zwischenablage:", err);
        toast.error("Kopieren fehlgeschlagen");
      });
  } catch (err) {
    console.error("Fehler beim Kopieren in die Zwischenablage:", err);
    toast.error("Kopieren fehlgeschlagen");
  }
};

// Sidebar ein-/ausklappen
const toggleSidebar = () => {
  isOpen.value = !isOpen.value;
  sidebarSettings.value.isOpen = isOpen.value;
  saveSidebarSettings();
  document.body.classList.toggle('sidebar-is-open', isOpen.value);
};

// Widget-Panels ein-/ausklappen
const toggleKurztexteCollapsed = () => {
  sidebarSettings.value.kurztexteCollapsed = !sidebarSettings.value.kurztexteCollapsed;
  saveSidebarSettings();
  // Event-Listener neu einrichten, wenn Panel geöffnet wird
  if (!sidebarSettings.value.kurztexteCollapsed) {
    nextTick(() => setupWidgetEventListeners());
  }
};

const toggleDurchwahlenCollapsed = () => {
  sidebarSettings.value.durchwahlenCollapsed = !sidebarSettings.value.durchwahlenCollapsed;
  saveSidebarSettings();
  // Event-Listener neu einrichten, wenn Panel geöffnet wird
  if (!sidebarSettings.value.durchwahlenCollapsed) {
    nextTick(() => setupWidgetEventListeners());
  }
};

// Einstellungen laden
const loadSidebarSettings = async () => {
  if (!authStore.isAuthenticated) return;

  try {
    const response = await axios.get(`${API_BASE_URL}sidebar_settings.php`, {
      withCredentials: true
    });

    if (response.data.status === 'success' && response.data.settings) {
      sidebarSettings.value = { ...sidebarSettings.value, ...response.data.settings };
      isOpen.value = sidebarSettings.value.isOpen;
      if (isOpen.value) {
        document.body.classList.add('sidebar-is-open');
      }
    }
  } catch (error) {
    console.error('Fehler beim Laden der Sidebar-Einstellungen:', error);
  }
};

// Einstellungen speichern
const saveSidebarSettings = async () => {
  if (!authStore.isAuthenticated) return;

  try {
    await axios.post(`${API_BASE_URL}sidebar_settings.php`, {
      settings: sidebarSettings.value
    }, {
      withCredentials: true
    });
  } catch (error) {
    console.error('Fehler beim Speichern der Sidebar-Einstellungen:', error);
  }
};

// Durchwahlen laden
const fetchDurchwahlen = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}extensions.php?action=get`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      globalDurchwahlen.value = response.data.durchwahlen || [];
      nextTick(() => setupWidgetEventListeners());
    }
  } catch (error) {
    console.error('Fehler beim Laden der Durchwahlen:', error);
  }
};

// Kurztexte laden
const fetchKurztexte = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}textsnippets.php`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      globalKurztexte.value = response.data.kurztexte || [];
      nextTick(() => setupWidgetEventListeners());
    }
  } catch (error) {
    console.error('Fehler beim Laden der Kurztexte:', error);
  }
};

// Dashboard-Route prüfen, um Sidebar ein-/auszublenden
const isDashboard = computed(() => {
  return route.path === '/dashboard';
});

// Modelle-Daten
const globalModelle = ref([]);
const showAllModelle = ref(false);

// Suchvariablen
const modellSearchQuery = ref('');

// Modelle laden
const fetchModelle = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}modelle.php`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      globalModelle.value = response.data.modelle || [];
      nextTick(() => setupWidgetEventListeners());
    }
  } catch (error) {
    console.error('Fehler beim Laden der Modelle:', error);
  }
};

// Berechnen der angezeigten Modelle
const displayedModelle = computed(() => {
  // Filtern nach Suchbegriff
  let filtered = globalModelle.value;
  if (modellSearchQuery.value) {
    const query = modellSearchQuery.value.toLowerCase();
    filtered = filtered.filter(
      item => item.modell.toLowerCase().includes(query) ||
              (item.sonstiges && item.sonstiges.toLowerCase().includes(query))
    );
  }
  // Bei aktivierter "Alle anzeigen" Option oder weniger als 5 Ergebnissen alle anzeigen
  if (showAllModelle.value || filtered.length <= 4) {
    return filtered;
  }
  // Sonst nur die ersten 4
  return filtered.slice(0, 4);
});

// Umschalten der Modelle-Anzeige
const toggleShowAllModelle = () => {
  showAllModelle.value = !showAllModelle.value;
  // Event-Listener nach Änderung der angezeigten Items neu einrichten
  nextTick(() => setupWidgetEventListeners());
};

// Modell bearbeiten (Platzhalter-Funktion)
const editModel = (model) => {
  // Diese Funktion kann später implementiert werden, falls benötigt
};

// Event-Listener bei Sucheingabe neu einrichten
watch(durchwahlSearchQuery, () => {
  nextTick(() => setupWidgetEventListeners());
});

watch(kurztextSearchQuery, () => {
  nextTick(() => setupWidgetEventListeners());
});

watch(modellSearchQuery, () => {
  nextTick(() => setupWidgetEventListeners());
});

// Provide für DurchwahlWidget und KurztexteWidget
provide('globalDurchwahlen', globalDurchwahlen);
provide('displayedDurchwahlen', displayedDurchwahlen);
provide('showAllDurchwahlen', showAllDurchwahlen);
provide('toggleShowAllDurchwahlen', toggleShowAllDurchwahlen);
provide('copyToClipboard', copyToClipboard);

provide('globalKurztexte', globalKurztexte);
provide('displayedKurztexte', displayedKurztexte);
provide('showAllKurztexte', showAllKurztexte);
provide('toggleShowAllKurztexte', toggleShowAllKurztexte);
provide('truncateText', truncateText);

provide('globalModelle', globalModelle);
provide('displayedModelle', displayedModelle);
provide('showAllModelle', showAllModelle);
provide('toggleShowAllModelle', toggleShowAllModelle);
provide('editModel', editModel);

// Bei Route-Änderung Sidebar auf Dashboard ausblenden
watch(() => route.path, (newPath) => {
  if (newPath === '/dashboard') {
    isOpen.value = false;
    document.body.classList.remove('sidebar-is-open');
  } else {
    isOpen.value = sidebarSettings.value.isOpen;
    document.body.classList.toggle('sidebar-is-open', isOpen.value);
  }
}, { immediate: true });

// Automatisches Schließen der Sidebar bei kleinen Bildschirmen
const checkScreenSize = () => {
  if (window.innerWidth <= 576 && isOpen.value) {
    isOpen.value = false;
    sidebarSettings.value.isOpen = false;
    document.body.classList.remove('sidebar-is-open');
    saveSidebarSettings();
  }
};

// Escape-Taste schließt Tooltip und Dialog
const handleKeyDown = (event) => {
  if (event.key === 'Escape') {
    hideTooltip();
    detailDialog.value.visible = false;
  }
};

// MutationObserver für DOM-Änderungen, um Event-Listener neu einzurichten
let observer;
const setupMutationObserver = () => {
  observer = new MutationObserver(() => {
    setupWidgetEventListeners();
  });

  const sidebarContent = document.querySelector('.sidebar-content');
  if (sidebarContent) {
    observer.observe(sidebarContent, {
      childList: true,
      subtree: true
    });
  }
};

// Beim Mounten Daten laden und Event-Listener einrichten
onMounted(() => {
  if (authStore.isAuthenticated) {
    loadSidebarSettings();
    fetchDurchwahlen();
    fetchKurztexte();
    fetchModelle();

    // Event-Listener für die Escape-Taste und Bildschirmgröße hinzufügen
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', checkScreenSize);

    // Initiale Überprüfung der Bildschirmgröße
    checkScreenSize();

    // MutationObserver einrichten
    setupMutationObserver();
  }
});

// Event-Listener entfernen, wenn Komponente zerstört wird
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyDown);
  window.removeEventListener('resize', checkScreenSize);
  document.body.classList.remove('sidebar-is-open');
  if (observer) {
    observer.disconnect();
  }
});
</script>

<style scoped>
.right-sidebar-wrapper {
  position: fixed;
  top: 64px; /* Anpassung an die Header-Höhe */
  right: -350px;
  width: 350px;
  height: calc(100vh - 110px); /* Angepasst für den Footer */
  z-index: 999;
  transition: right 0.3s ease, width 0.3s ease;
  display: flex;
  flex-direction: column;
}

.sidebar-open {
  right: 0;
  box-shadow: -8px 0 25px rgba(0, 0, 0, 0.25);
}

.toggle-btn {
  position: absolute;
  top: 50%;
  left: -30px;
  width: 30px;
  height: 60px;
  background: rgba(35, 35, 40, 0.85);
  border-radius: 8px 0 0 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-right: none;
  transform: translateY(-50%);
  box-shadow: -4px 0 15px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  z-index: 1;
  transition: all 0.2s ease;
}

.toggle-btn:hover {
  background: rgba(50, 50, 55, 0.9);
  color: rgba(76, 175, 80, 0.9);
  left: -32px;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  background: rgba(30, 30, 35, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
  box-shadow: -8px 0 25px rgba(0, 0, 0, 0.2);
}

.sidebar-card {
  background: rgba(35, 35, 40, 0.7) !important;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.sidebar-card-title {
  padding: 12px;
  font-size: 1rem;
  background: rgba(25, 25, 30, 0.7);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.sidebar-card-title:hover {
  background: rgba(35, 35, 40, 0.8);
}

.search-container {
  padding: 8px 12px;
  background: rgba(30, 30, 35, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.search-field {
  margin-bottom: 0;
  font-size: 0.85rem;
}

.search-field :deep(.v-field__prepend-inner) {
  opacity: 0.7;
}

.search-field :deep(.v-field__field) {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

/* Tooltip-Styling */
.custom-tooltip {
  position: fixed;
  z-index: 9999;
  background-color: rgba(35, 35, 45, 0.95);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  max-width: 350px;
  min-width: 200px;
  padding: 12px;
  pointer-events: auto;
  backdrop-filter: blur(10px);
}

.tooltip-content {
  max-height: 500px;
  overflow-y: auto;
}

.tooltip-header {
  font-weight: 600;
  font-size: 16px;
  color: rgba(76, 175, 80, 0.9);
  margin-bottom: 8px;
}

.tooltip-field {
  margin-bottom: 10px;
}

.tooltip-label {
  font-weight: 500;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 2px;
}

.tooltip-value {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
  white-space: pre-wrap;
}

/* Dialog-Styling */
.detail-dialog {
  background-color: rgba(35, 35, 45, 0.95) !important;
  border: 1px solid rgba(76, 175, 80, 0.3);
  backdrop-filter: blur(10px);
}

.dialog-title {
  background-color: rgba(25, 25, 30, 0.7);
  color: rgba(76, 175, 80, 0.9);
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dialog-content {
  padding: 24px;
}

.dialog-field {
  margin-bottom: 16px;
}

.dialog-label {
  font-weight: 500;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 4px;
}

.dialog-value {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

.dialog-multiline {
  white-space: pre-wrap;
  background: rgba(20, 20, 25, 0.3);
  padding: 12px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* Scrollbar-Styling */
.sidebar-content::-webkit-scrollbar,
.tooltip-content::-webkit-scrollbar {
  width: 6px;
}

.sidebar-content::-webkit-scrollbar-track,
.tooltip-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

.sidebar-content::-webkit-scrollbar-thumb,
.tooltip-content::-webkit-scrollbar-thumb {
  background: rgba(76, 175, 80, 0.3);
  border-radius: 3px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover,
.tooltip-content::-webkit-scrollbar-thumb:hover {
  background: rgba(76, 175, 80, 0.5);
}

/* Spezifisches Styling für die Widget-Items zur besseren Erkennung der Interaktivität */
:deep(.model-item),
:deep(.extension-item),
:deep(.snippet-item) {
  cursor: pointer;
  position: relative;
}

:deep(.model-item:hover),
:deep(.extension-item:hover),
:deep(.snippet-item:hover) {
  background-color: rgba(35, 35, 45, 0.7) !important;
  transform: translateY(-2px) !important;
}

/* Anpassungen für verschiedene Bildschirmgrößen */
@media (max-width: 1200px) {
  .right-sidebar-wrapper {
    width: 320px;
    right: -320px;
  }
}

@media (max-width: 992px) {
  .right-sidebar-wrapper {
    width: 280px;
    right: -280px;
  }
}

@media (max-width: 768px) {
  .right-sidebar-wrapper {
    top: 60px; /* Kleinerer Abstand auf mobilen Geräten */
    height: calc(100vh - 100px); /* Angepasste Höhe für mobile Geräte */
    width: 260px;
    right: -260px;
  }

  .sidebar-content {
    padding: 10px;
  }

  .custom-tooltip {
    max-width: 280px;
  }
}

@media (max-width: 576px) {
  .right-sidebar-wrapper {
    width: 240px;
    right: -240px;
  }

  .toggle-btn {
    height: 50px;
    width: 25px;
    left: -25px;
  }

  .toggle-btn:hover {
    left: -27px;
  }

  .sidebar-card-title {
    padding: 10px;
    font-size: 0.9rem;
  }

  .tooltip-content,
  .dialog-content {
    padding: 10px;
  }
}

/* Globale Klassen für die Anpassung des Hauptinhalts */
:global(.sidebar-is-open .main-content) {
  transition: padding-right 0.3s ease;
  padding-right: 350px;
}

@media (max-width: 1200px) {
  :global(.sidebar-is-open .main-content) {
    padding-right: 320px;
  }
}

@media (max-width: 992px) {
  :global(.sidebar-is-open .main-content) {
    padding-right: 280px;
  }
}

@media (max-width: 768px) {
  :global(.sidebar-is-open .main-content) {
    padding-right: 260px;
  }
}

@media (max-width: 576px) {
  :global(.sidebar-is-open .main-content) {
    padding-right: 0; /* Bei sehr kleinen Bildschirmen keinen Abstand, Sidebar überlappt */
  }
}
</style>
